import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Search, X, ChevronLeft, Users, Tag, Check } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { Project, FortnoxCustomer } from '@/types/api'

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ProjectsBulkPage() {
  const queryClient = useQueryClient()

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<Project[]>('/projects'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: customers = [] } = useQuery({
    queryKey: ['fortnox', 'customers'],
    queryFn:  () => api.get<FortnoxCustomer[]>('/fortnox/customers'),
  })

  const [search,        setSearch]        = useState('')
  const [statusFilter,  setStatusFilter]  = useState<Project['status'] | 'all'>('all')
  const [internalOnly,  setInternalOnly]  = useState(false)
  // WI-11899: filter for projects that haven't been worked on for at least
  // N days. Empty/0 disables the filter. Projects with no time entries ever
  // (daysSinceWorkedOn === null) always pass when the filter is on.
  const [minIdleDays,   setMinIdleDays]   = useState<number | ''>('')
  const [selected,      setSelected]      = useState<Set<number>>(new Set())
  const [dialog,        setDialog]        = useState<'customer' | 'status' | null>(null)

  const bulkMutation = useMutation({
    mutationFn: (body: { ids: number[]; customerId?: number | null; status?: string }) =>
      api.patch<{ updated: number }>('/projects/bulk', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setDialog(null)
      setSelected(new Set())
    },
  })

  const statusCounts: Record<string, number> = { all: projects.length }
  for (const p of projects) statusCounts[p.status] = (statusCounts[p.status] ?? 0) + 1

  const STATUS_FILTERS: { value: Project['status'] | 'all'; label: string }[] = [
    { value: 'all',       label: 'All' },
    { value: 'active',    label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold',   label: 'On hold' },
  ]

  const q = search.toLowerCase()
  const minIdle = typeof minIdleDays === 'number' ? minIdleDays : 0
  const visible = projects.filter(p =>
    (statusFilter === 'all' || p.status === statusFilter) &&
    (!internalOnly || p.customerId == null) &&
    (minIdle <= 0 || (p.daysSinceWorkedOn ?? Number.POSITIVE_INFINITY) >= minIdle) &&
    (!q ||
      p.name.toLowerCase().includes(q) ||
      (p.customerName ?? '').toLowerCase().includes(q) ||
      (p.fortnoxProjectNumber ?? '').includes(q))
  )

  const headerCheckRef      = useRef<HTMLInputElement>(null)
  const allVisibleSelected  = visible.length > 0 && visible.every(p => selected.has(p.id))
  const someVisibleSelected = visible.some(p => selected.has(p.id))

  useEffect(() => {
    if (headerCheckRef.current)
      headerCheckRef.current.indeterminate = someVisibleSelected && !allVisibleSelected
  }, [someVisibleSelected, allVisibleSelected])

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allVisibleSelected) visible.forEach(p => next.delete(p.id))
      else                     visible.forEach(p => next.add(p.id))
      return next
    })
  }

  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const selectedIds      = [...selected]
  const selectedProjects = projects.filter(p => selected.has(p.id))

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link
            to="/projects"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              color: 'var(--ns-text-muted)', fontSize: 12.5,
              textDecoration: 'none', marginBottom: 8,
            }}
          >
            <ChevronLeft size={13} />Back to Projects
          </Link>
          <div className="page-eyebrow">Workspace</div>
          <h1 className="page-title">Bulk Edit Projects</h1>
          <div className="page-subtitle">Select projects and apply changes to all at once</div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {/* Toolbar */}
        <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 30, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', width: 240 }}>
            <Search size={13} style={{ color: 'var(--ns-text-subtle)', flexShrink: 0 }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects…"
              style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: 'var(--ns-text-subtle)', display: 'flex' }}>
                <X size={12} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 4 }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                className={`chip${statusFilter === f.value ? ' active' : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label} <span className="tabular" style={{ opacity: 0.6, fontSize: 11.5, marginLeft: 2 }}>{statusCounts[f.value] ?? 0}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={`chip${internalOnly ? ' active' : ''}`}
              onClick={() => setInternalOnly(v => !v)}
            >
              Internal only
            </button>
          </div>

          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              paddingLeft: 10, paddingRight: 8, height: 30,
              border: '1px solid var(--ns-border)', borderRadius: 6,
              background: 'var(--ns-surface)',
            }}
            title="Show only projects that haven't been worked on in this many days"
          >
            <span style={{ fontSize: 12.5, color: 'var(--ns-text-muted)', whiteSpace: 'nowrap' }}>
              Idle ≥
            </span>
            <input
              type="number"
              min={0}
              step={1}
              placeholder="—"
              value={minIdleDays}
              onChange={e => {
                const v = e.target.value
                setMinIdleDays(v === '' ? '' : Math.max(0, Number(v)))
              }}
              style={{
                width: 60, height: '100%', border: 'none',
                background: 'transparent', outline: 'none',
                fontSize: 13, textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
            <span style={{ fontSize: 12, color: 'var(--ns-text-subtle)' }}>d</span>
            {minIdleDays !== '' && minIdleDays > 0 && (
              <button
                onClick={() => setMinIdleDays('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ns-text-subtle)', display: 'flex' }}
                aria-label="Clear idle filter"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div style={{ flex: 1 }} />

          {selectedIds.length > 0 && (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--ns-text-muted)', alignSelf: 'center' }}>
                {selectedIds.length} selected
              </span>
              <button className="btn btn-ghost" style={{ gap: 6 }} onClick={() => setDialog('customer')}>
                <Users size={13} />Map to customer
              </button>
              <button className="btn btn-ghost" style={{ gap: 6 }} onClick={() => setDialog('status')}>
                <Tag size={13} />Set status
              </button>
            </>
          )}

          <span className="muted tabular" style={{ fontSize: 11.5, alignSelf: 'center' }}>
            {visible.length} of {projects.length}
          </span>
        </div>

        {isLoading ? (
          <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Loading…</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    ref={headerCheckRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th>Project</th>
                <th>Customer</th>
                <th style={{ width: 100 }}>Idle</th>
                <th style={{ width: 100 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(p => (
                <tr
                  key={p.id}
                  onClick={() => toggleOne(p.id)}
                  style={{ cursor: 'pointer', background: selected.has(p.id) ? 'var(--ns-brand-soft)' : undefined }}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ fontWeight: 500 }}>{p.name}</td>
                  <td className="muted">
                    {p.customerName ?? <span style={{ fontStyle: 'italic' }}>Internal</span>}
                  </td>
                  <td>
                    {p.daysSinceWorkedOn == null ? (
                      <span className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>never</span>
                    ) : (
                      <span className="tabular" style={{
                        fontSize: 13,
                        color: p.daysSinceWorkedOn >= 90 ? 'var(--ns-err)'
                          : p.daysSinceWorkedOn >= 30 ? 'var(--ns-warn)'
                          : 'var(--ns-text-muted)',
                      }}>
                        {p.daysSinceWorkedOn}<span className="muted" style={{ fontSize: 11, marginLeft: 2 }}>d</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge${p.status === 'active' ? ' badge-ok' : ''}`}>
                      {p.status === 'on_hold' ? 'on hold' : p.status}
                    </span>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                    No projects match the current search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {dialog === 'customer' && (
        <CustomerDialog
          selectedProjects={selectedProjects}
          customers={customers}
          onClose={() => setDialog(null)}
          onApply={(customerId, onlyUnmapped) => {
            const ids = onlyUnmapped
              ? selectedIds.filter(id => projects.find(p => p.id === id)?.customerId == null)
              : selectedIds
            bulkMutation.mutate({ ids, customerId })
          }}
          isPending={bulkMutation.isPending}
        />
      )}

      {dialog === 'status' && (
        <StatusDialog
          selectedCount={selectedIds.length}
          onClose={() => setDialog(null)}
          onApply={status => bulkMutation.mutate({ ids: selectedIds, status })}
          isPending={bulkMutation.isPending}
        />
      )}
    </div>
  )
}

// ─── Customer dialog ──────────────────────────────────────────────────────────

interface CustomerDialogProps {
  selectedProjects: Project[]
  customers:        FortnoxCustomer[]
  onClose:          () => void
  onApply:          (customerId: number, onlyUnmapped: boolean) => void
  isPending:        boolean
}

function CustomerDialog({ selectedProjects, customers, onClose, onApply, isPending }: CustomerDialogProps) {
  const [searchQ, setSearchQ] = useState('')
  const [picked,  setPicked]  = useState<FortnoxCustomer | null>(null)

  const filtered = customers.filter(c =>
    !searchQ || c.name.toLowerCase().includes(searchQ.toLowerCase())
  )

  const conflictCount  = picked ? selectedProjects.filter(p => p.customerId != null && p.customerId !== picked.id).length : 0
  const unmappedCount  = selectedProjects.filter(p => p.customerId == null).length
  const total          = selectedProjects.length

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 440, maxHeight: '80vh', background: 'var(--ns-surface)',
        border: '1px solid var(--ns-border)', borderRadius: 10,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)', zIndex: 50,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--ns-border)', flexShrink: 0 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Map to customer</h2>
          <p className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
            Select a customer to assign to {total} project{total !== 1 ? 's' : ''}.
          </p>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--ns-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 32, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)' }}>
            <Search size={13} style={{ color: 'var(--ns-text-subtle)', flexShrink: 0 }} />
            <input
              autoFocus
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search customers…"
              style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }}
            />
            {searchQ && (
              <button onClick={() => setSearchQ('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: 'var(--ns-text-subtle)', display: 'flex' }}>
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Customer list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.map(c => (
            <div
              key={c.id}
              onClick={() => setPicked(c)}
              style={{
                padding: '8px 20px', fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: picked?.id === c.id ? 'var(--ns-brand-soft)' : undefined,
                color:      picked?.id === c.id ? 'var(--ns-brand-strong)' : undefined,
              }}
              className="dropdown-item"
            >
              <span style={{ fontWeight: picked?.id === c.id ? 600 : 400 }}>{c.name}</span>
              {picked?.id === c.id && <Check size={14} />}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '12px 20px', fontSize: 12.5, color: 'var(--ns-text-muted)' }}>No matches</div>
          )}
        </div>

        {/* Footer — confirmation when a customer is picked */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ns-border)', flexShrink: 0, background: picked ? 'var(--ns-surface-2)' : undefined }}>
          {picked ? (
            <>
              <p style={{ fontSize: 12.5, marginBottom: 12, lineHeight: 1.6 }}>
                Are you sure you want to apply <strong>{picked.name}</strong> to these{' '}
                <strong>{total}</strong> project{total !== 1 ? 's' : ''}?
                {conflictCount > 0 && (
                  <> <span style={{ color: 'var(--ns-warn)' }}>
                    {conflictCount} {conflictCount === 1 ? 'is' : 'are'} already assigned to a different customer.
                  </span></>
                )}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={onClose} disabled={isPending}>Cancel</button>
                {unmappedCount > 0 && unmappedCount < total && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => onApply(picked.id, true)}
                    disabled={isPending}
                  >
                    Assign only unmapped ({unmappedCount})
                  </button>
                )}
                <button
                  className="btn btn-brand"
                  onClick={() => onApply(picked.id, false)}
                  disabled={isPending}
                >
                  {isPending ? 'Assigning…' : `Assign (${total})`}
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ─── Status dialog ────────────────────────────────────────────────────────────

interface StatusDialogProps {
  selectedCount: number
  onClose:       () => void
  onApply:       (status: string) => void
  isPending:     boolean
}

function StatusDialog({ selectedCount, onClose, onApply, isPending }: StatusDialogProps) {
  const [status, setStatus] = useState<'active' | 'completed' | 'on_hold' | null>(null)

  const OPTIONS = [
    { value: 'active'    as const, label: 'Active' },
    { value: 'completed' as const, label: 'Completed' },
    { value: 'on_hold'   as const, label: 'On hold' },
  ]

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 360, background: 'var(--ns-surface)',
        border: '1px solid var(--ns-border)', borderRadius: 10,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)', zIndex: 50,
      }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--ns-border)' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Set status</h2>
          <p className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
            Apply a status to {selectedCount} project{selectedCount !== 1 ? 's' : ''}.
          </p>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', gap: 8 }}>
          {OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatus(opt.value)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border:      `1px solid ${status === opt.value ? 'var(--ns-brand)' : 'var(--ns-border)'}`,
                background:  status === opt.value ? 'var(--ns-brand-soft)' : 'var(--ns-surface)',
                color:       status === opt.value ? 'var(--ns-brand-strong)' : 'var(--ns-text)',
                fontWeight:  status === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isPending}>Cancel</button>
          <button
            className="btn btn-brand"
            disabled={!status || isPending}
            onClick={() => status && onApply(status)}
          >
            {isPending ? 'Applying…' : `Apply to ${selectedCount}`}
          </button>
        </div>
      </div>
    </>
  )
}
